import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ModalWindowUI } from '@cube3/ui/src/Modal/ModalWindowUI';
import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useVisitingFolder } from '@cube3/state/src/redux/components/Hooks/clipboard/useVisitingFolder';
import { uuidv4 } from '@cube3/state/src/utils/uuid';
import { useModalActions } from '../../layout/Modals/modalActions';
import { getEnvVar } from '../../../../utils/getEnvVar';
import { FrameioPickedFile, isOutboundMessage } from './protocol';

/**
 * Embeds the Frame.io picker, which runs on its own origin because Frame.io's API
 * refuses browser calls from ours. Unlike the Dropbox chooser this iframe is the UI,
 * not a hidden bridge to a vendor pop-up, so it fills a modal.
 */
export const FrameioChooser = () => {
  const { closeAllModals } = useModalActions();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const pickerOrigin = `${location.protocol}//${getEnvVar('FRAMEIO_CHOOSER_HOST') || 'frame-io.localhost'}`;

  // The picker will not hand over files until it can identify its embedder, and
  // document.referrer is not dependable enough for it to rely on.
  const pickerUrl = useMemo(
    () => `${pickerOrigin}/?host=${encodeURIComponent(window.location.origin)}`,
    [pickerOrigin]
  );

  const [visitingFolderId] = useVisitingFolder({ getAncestors: false });

  const [createResource] = useCreateResource__ALPHA({
    resourceType: 'asset',
    cacheInvalidator: useCallback(
      () =>
        visitingFolderId
          ? [
              {
                type: 'folder' as const,
                id: visitingFolderId,
                relationship: 'assets'
              }
            ]
          : [],
      [visitingFolderId]
    )
  });

  const onCreateAssets = useCallback(
    (files: FrameioPickedFile[]) => {
      createResource(
        files.map((file) => ({
          type: 'asset' as const,
          temporaryId: uuidv4(),
          display_name: file.name,
          source_url: file.source_url,
          relationships: {
            parent: { data: { type: 'node', id: visitingFolderId } }
          }
        }))
      );
    },
    [visitingFolderId, createResource]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Origin alone is not enough: any window on the picker's origin could post.
      // Requiring the event to come from this iframe closes that.
      if (event.origin !== pickerOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isOutboundMessage(event.data)) return;

      switch (event.data.type) {
        case 'FRAMEIO_FILES_SELECTED':
          // Deliberately not logged: these payloads carry pre-signed URLs that need
          // no credentials, and console arguments are collected as Sentry breadcrumbs.
          onCreateAssets(event.data.files);
          closeAllModals();
          break;

        case 'FRAMEIO_CANCELLED':
          closeAllModals();
          break;

        case 'FRAMEIO_ERROR':
          console.error('[frameio] picker reported an error:', event.data.message);
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pickerOrigin, onCreateAssets, closeAllModals]);

  return (
    <ModalWindowUI
      hideHeader={true}
      onCloseEvent={closeAllModals}
      // Deliberately inset rather than the default near-fullscreen sizing: the picker
      // is another product, and filling the viewport makes it read as part of Cube.
      contentSize={{
        width: 'min(1040px, calc(100vw - 128px))',
        height: 'min(720px, calc(100vh - 160px))'
      }}
    >
      <iframe
        ref={iframeRef}
        src={pickerUrl}
        title="Upload from Frame.io"
        // allow-popups and -to-escape-sandbox are required: Adobe IMS refuses to
        // render in an iframe, so the picker signs in through a pop-up.
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
        style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
      />
    </ModalWindowUI>
  );
};
